import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, TableCell, TableRow, Tooltip } from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LaunchIcon from "@mui/icons-material/Launch";
import { AnnouncementPurpleIcon } from "../../icons";
import { TypographyNormal, TypographySmall } from "../customComponent";
import { colorStyling } from "../../helpers/color";
import { switchBackgroundColorBasedOnStatus } from "../../helpers/switchColor";
import { SET_ANNOUNCEMENT_DETAIL } from "../../store/actionTypes/announcementActionType";
import { PriorityIcon } from "../../helpers/scheduleHelper";

const AnnouncementItem = ({ announcement, dispatch, openContent, index }) => {
  const navigate = useNavigate();
  const { announcement: detail } = useSelector((state) => state.announcement);

  const [hoveredContent, setHoveredContent] = useState(false);

  const { t } = useTranslation("announcement");

  const handleOpenContentDetail = (e, id) => {
    e.stopPropagation();
    openContent(id);
  };

  const navigateToAnnouncementDetail = () => {
    const newDetail = {
      ...announcement,
      ...(detail.id === announcement.id && {
        screenGroups: detail.screenGroups,
        screens: detail.screens,
      }),
    };
    dispatch({ type: SET_ANNOUNCEMENT_DETAIL, payload: newDetail });
    navigate("/announcement/detail/" + announcement.id);
  };

  return (
    <TableRow
      key={announcement.name + announcement.id}
      onClick={() => navigateToAnnouncementDetail()}
      sx={{
        cursor: "pointer",
        backgroundColor:
          (index + 1) % 2 !== 0
            ? colorStyling.white.hover
            : colorStyling.white.full,
        "&:hover": {
          backgroundColor: colorStyling.white.lightblue,
        },
      }}
    >
      <TableCell sx={{ pl: 3, pr: 0 }}>
        <Box width={48} height={48}>
          <AnnouncementPurpleIcon />
        </Box>
      </TableCell>
      <TableCell>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          {announcement.isApproved ? (
            <Tooltip title={t("item.approvedTip")} placement="top">
              <CheckCircleOutlineIcon
                fontSize="small"
                sx={{
                  mr: 0.5,
                  color: switchBackgroundColorBasedOnStatus("APPROVED"),
                  borderRadius: "16px",
                }}
              />
            </Tooltip>
          ) : (
            <Tooltip title={t("item.notApprovedTip")} placement="top">
              <ErrorOutlineIcon
                fontSize="small"
                sx={{
                  mr: 0.5,
                  color: switchBackgroundColorBasedOnStatus("NOT_APPROVED"),
                  borderRadius: "16px",
                }}
              />
            </Tooltip>
          )}

          <PriorityIcon level={announcement?.priority?.name} />

          <Tooltip
            title={announcement?.name?.length > 30 ? announcement?.name : ""}
            placement="top-start"
          >
            <TypographyNormal>
              {announcement?.name?.length > 30
                ? announcement?.name?.substring(0, 27).trim() + "..."
                : announcement?.name}
            </TypographyNormal>
          </Tooltip>
        </Box>
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <TypographySmall
            sx={{
              display: "flex",
              alignItems: "center",
              borderRadius: "15px",
              color:
                announcement.isActive === true
                  ? colorStyling.green
                  : colorStyling.black,
              backgroundColor:
                announcement.isActive === true
                  ? colorStyling.white.active
                  : colorStyling.white.inactive,
              p: 1,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            <FiberManualRecordIcon
              sx={{
                color:
                  announcement.isActive === true
                    ? colorStyling.green
                    : colorStyling.gray.normal,
                fontSize: 8,
                mr: 1,
              }}
            />
            {announcement.isActive
              ? t("item.statusONLabel")
              : t("item.statusOFFLabel")}
          </TypographySmall>
        </Box>
      </TableCell>
      <Tooltip title={t("contentDetailHint")} placement="top-start">
        <TableCell
          onClick={(event) =>
            handleOpenContentDetail(event, announcement.contentId)
          }
          onMouseEnter={() => setHoveredContent(true)}
          onMouseLeave={() => setHoveredContent(false)}
          sx={{
            ...(hoveredContent && {
              color: colorStyling.primary,
              textDecoration: "underline",
              fontWeight: 600,
            }),
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            {announcement?.content?.name}
            <LaunchIcon
              fontSize="xs"
              sx={{
                color: hoveredContent
                  ? colorStyling.primary
                  : colorStyling.gray.label,
              }}
            />
          </Box>
        </TableCell>
      </Tooltip>
    </TableRow>
  );
};

export default AnnouncementItem;
